import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, Search, Loader2, AlertTriangle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

export type PublishWebsite = {
  id: string;
  name: string;
  domain: string | null;
  primary_language: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  websites: PublishWebsite[];
  articleCount: number;
  articleLanguages: string[]; // unique langs across selected articles
  onConfirm: (websiteIds: string[]) => Promise<void>;
};

export default function MultiSitePublishDialog({
  open, onOpenChange, websites, articleCount, articleLanguages, onConfirm,
}: Props) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return websites;
    return websites.filter(
      (w) => w.name.toLowerCase().includes(q) || (w.domain || "").toLowerCase().includes(q),
    );
  }, [websites, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const allSelected = filtered.every((w) => selected.has(w.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach((w) => next.delete(w.id));
      else filtered.forEach((w) => next.add(w.id));
      return next;
    });
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(Array.from(selected));
      setSelected(new Set());
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Publish to websites</DialogTitle>
          <DialogDescription>
            Choose one or more sites where {articleCount} article{articleCount > 1 ? "s" : ""} will be published.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            <span className="text-muted-foreground">Article languages:</span>
            {articleLanguages.map((l) => (
              <Badge key={l} variant="outline" className="uppercase text-[10px]">{l}</Badge>
            ))}
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search sites by name or domain…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-xs"
            />
          </div>

          <div className="flex items-center justify-between text-xs">
            <button onClick={toggleAllVisible} className="text-primary hover:underline">
              {filtered.every((w) => selected.has(w.id)) && filtered.length > 0 ? "Clear" : "Select all"} ({filtered.length})
            </button>
            <span className="text-muted-foreground">{selected.size} selected</span>
          </div>

          <ScrollArea className="h-72 rounded-md border border-border">
            <div className="divide-y divide-border">
              {filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">No sites match your search.</p>
              )}
              {filtered.map((w) => {
                const langMismatch = !articleLanguages.includes(w.primary_language);
                const checked = selected.has(w.id);
                return (
                  <label
                    key={w.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent/40 cursor-pointer transition-colors"
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(w.id)} />
                    <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground truncate">{w.name}</p>
                      {w.domain && (
                        <p className="text-[10px] text-muted-foreground truncate">{w.domain}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="uppercase text-[9px] shrink-0">
                      {w.primary_language}
                    </Badge>
                    {langMismatch && (
                      <span title="Language differs from article" className="text-warning shrink-0">
                        <AlertTriangle className="h-3 w-3" />
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selected.size === 0 || submitting}>
            {submitting ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Publishing…</>
            ) : (
              <>Publish to {selected.size} site{selected.size > 1 ? "s" : ""}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
